import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useChat } from '../context/ChatContext';

const { FiMessageCircle, FiX, FiSend, FiPhone } = FiIcons;

const ChatBot = () => {
  const { isOpen, setIsOpen, messages, sendMessage, isLoading } = useChat();
  const [inputValue, setInputValue] = useState('');
  const [showPhoneInput, setShowPhoneInput] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim()) {
      if (showPhoneInput && phoneNumber.trim()) {
        sendMessage(inputValue, phoneNumber);
        setShowPhoneInput(false);
        setPhoneNumber('');
      } else {
        sendMessage(inputValue);
      }
      setInputValue('');
    }
  };

  const handlePhoneSubmit = () => {
    if (phoneNumber.trim()) {
      setShowPhoneInput(false);
      sendMessage(inputValue, phoneNumber);
      setInputValue('');
      setPhoneNumber('');
    }
  };

  const needsPhoneNumber = (message) => {
    const phoneKeywords = ['text', 'sms', 'send', 'schedule', 'appointment'];
    return phoneKeywords.some(keyword => message.toLowerCase().includes(keyword));
  };

  const handleSendClick = () => {
    if (inputValue.trim() && needsPhoneNumber(inputValue) && !phoneNumber) {
      setShowPhoneInput(true);
    } else {
      handleSubmit({ preventDefault: () => {} });
    }
  };

  return (
    <>
      {/* Chat Toggle Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 bg-dealership-blue text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition-colors duration-200 z-50"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <SafeIcon 
          icon={isOpen ? FiX : FiMessageCircle} 
          className="w-6 h-6" 
        />
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-24 right-6 w-96 max-w-[calc(100vw-3rem)] bg-white rounded-lg shadow-2xl border border-gray-200 z-50"
          >
            {/* Header */}
            <div className="bg-dealership-blue text-white p-4 rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiMessageCircle} className="w-5 h-5" />
                  <span className="font-medium">Virtual Assistant</span>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              </div>
              <p className="text-blue-100 text-xs mt-1">
                Available 24/7 • Powered by AI
              </p>
            </div>

            {/* Messages */}
            <div className="h-80 overflow-y-auto p-4 space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-xs px-3 py-2 rounded-lg text-sm ${
                      message.sender === 'user'
                        ? 'bg-dealership-blue text-white'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    <p className="whitespace-pre-line">{message.text}</p>
                    <p className={`text-xs mt-1 ${
                      message.sender === 'user' ? 'text-blue-100' : 'text-gray-500'
                    }`}>
                      {new Date(message.timestamp).toLocaleTimeString([], { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </p>
                  </div>
                </div>
              ))}
              
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 px-3 py-2 rounded-lg">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Phone Number Input */}
            {showPhoneInput && (
              <div className="px-4 py-2 bg-blue-50 border-t border-gray-200">
                <p className="text-sm text-gray-600 mb-2">
                  To send you information via text, please provide your phone number:
                </p>
                <div className="flex space-x-2">
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="(555) 123-4567"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-dealership-blue"
                  />
                  <button
                    onClick={handlePhoneSubmit}
                    className="bg-dealership-blue text-white px-3 py-2 rounded-md text-sm hover:bg-blue-700 transition-colors"
                  >
                    <SafeIcon icon={FiPhone} className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Input */}
            <form onSubmit={handleSubmit} className="p-4 border-t border-gray-200">
              <div className="flex space-x-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Ask about vehicles, financing, test drives..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-dealership-blue"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={handleSendClick}
                  disabled={!inputValue.trim() || isLoading}
                  className="bg-dealership-blue text-white px-3 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <SafeIcon icon={FiSend} className="w-4 h-4" />
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatBot;